import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Editor } from "@progress/kendo-react-editor";
import { updatedSchema } from "../../../../editor/editor-schema/schema";
import { formTools } from "../../../../editor/editorSettings/editorToolsSettings";
import { createEditorEventHandlers, setUpEditorView } from "../../../../editor/editorSettings/editorViewSetting";
import { insertSpanNode, replaceSpanNode } from "../../../../editor/editorUtils/editorInsertUtils";
import { generateUniqueId } from "../../../../editor/util";
import { getUserProfileControlsData } from "../../../../shared/services/auth.service";
import { useWorkflowContext } from "../../WorkflowContextProvider";
import EmailChipsPopup from "./EmailChipsPopup";
import {
  EDITOR_KEYS,
  INITIAL_VIEW_POPUP_STATE,
  TRIGGER_KEY_TO_SECTION,
  VIEW_POPUP_TYPES,
  getAvailableFields,
} from "./EmailFlowConstants";

/**
 * Email Body Editor Component
 * Rich text editor for composing email body content
 * @param {Object} props
 * @param {string} props.value - The HTML content of the email body
 * @param {Function} props.onChange - Callback when content changes (receives html string)
 * @param {string} props.placeholder - Placeholder text when empty
 */
const EmailBodyEditor = ({ value = "<p>Hello</p>", onChange, placeholder = "Compose your email body here..." }) => {
  const [viewPopUps, setViewPopUps] = useState(INITIAL_VIEW_POPUP_STATE);
  const viewRef = useRef(null);
  const editorRef = useRef(null);
  const viewPopUpsRef = useRef(viewPopUps);
  viewPopUpsRef.current = viewPopUps;
  const keydownHandlerRef = useRef(null);
  const { user } = useSelector((state) => state.auth);
  const { workflowCustomVariables, originalWorkflowVariables } = useSelector((state) => state.taskSidebar);
  const { workflowDefaults } = useWorkflowContext();
  const [userProfileFields, setUserProfileFields] = useState([]);

  useEffect(() => {
    getFields();
  }, []);

  const getFields = async () => {
    const response = await getUserProfileControlsData({ companyId: user.companyId, drutasUserId: user.id });
    if (response?.WorkInformations) {
      setUserProfileFields(response?.WorkInformations);
    }
  };

  /**
   * Custom mount handler for ProseMirror editor
   * Adds custom schema, input rules, and image resizing
   */
  const handleMount = (event) => {
    try {
      const state = event.viewProps.state;
      let { schema } = state;
      const customSchema = updatedSchema(schema);

      // Create custom event handlers
      const customEventHandlers = createEditorEventHandlers({
        ...event.viewProps.handleDOMEvents,
        keydown: (view, domEvent) => keydownHandlerRef.current?.(view, domEvent),
        click: (view, domEvent) => handleClickCallback(view, domEvent),
      });

      // Set up the editor view using the imported function
      viewRef.current = setUpEditorView(event, customSchema, { value }, customEventHandlers);

      return viewRef.current;
    } catch (error) {
      console.error("Error mounting editor:", error);
      return null;
    }
  };

  const openChipPopupForTriggerKey = async (domEvent, triggerKey) => {
    const section = TRIGGER_KEY_TO_SECTION[triggerKey];
    if (!section) return;
    domEvent.preventDefault();
    const id = await generateUniqueId(user.id);
    await insertSpanNode(editorRef, triggerKey, id, "at-class");
    const anchorEl = document.getElementById(id);
    setViewPopUps({
      ref: anchorEl,
      visible: true,
      type: VIEW_POPUP_TYPES.EMAIL_CHIPS,
      tempId: id,
      defaultSection: section,
    });
  };

  keydownHandlerRef.current = async (view, domEvent) => {
    const { key } = domEvent;
    const pop = viewPopUpsRef.current;
    if (pop.visible && pop.type === VIEW_POPUP_TYPES.EMAIL_CHIPS && key === "Escape") {
      domEvent.preventDefault();
      domEvent.stopPropagation();
      setViewPopUps(INITIAL_VIEW_POPUP_STATE);
      return;
    }
    if (key === EDITOR_KEYS.TILDE || key === EDITOR_KEYS.AT_SYMBOL || key === EDITOR_KEYS.HASH) {
      await openChipPopupForTriggerKey(domEvent, key);
    }
  };

  /**
   * Handle click events in the editor to open chips popup for @ symbols and chips
   * @param {Object} view - ProseMirror editor view
   * @param {Event} domEvent - DOM click event
   * @author Abhishek Dhillon
   */
  const handleClickCallback = (view, domEvent) => {
    const target = domEvent.target;

    // Check if clicked element is an @ symbol or chip
    if (target && (target.classList.contains("at-class") || target.classList.contains("email-chip"))) {
      const chipId = target.id;
      if (chipId) {
        const defaultSection =
          target.classList.contains("at-class") && TRIGGER_KEY_TO_SECTION[target.textContent]
            ? TRIGGER_KEY_TO_SECTION[target.textContent]
            : VIEW_POPUP_TYPES.CHIPS;
        setViewPopUps({
          ref: target,
          visible: true,
          type: VIEW_POPUP_TYPES.EMAIL_CHIPS,
          tempId: chipId,
          defaultSection,
        });
      }
    }
  };

  /**
   * Handle editor content change
   */
  const handleChange = (e) => {
    if (onChange) {
      onChange(e.html);
    }
  };

  return (
    <div className='email-body-editor dt-kendo-editor'>
      <Editor
        value={value}
        onChange={handleChange}
        defaultEditMode='div'
        tools={formTools}
        onMount={handleMount}
        placeholder={placeholder}
        ref={editorRef}
      />
      {userProfileFields.length > 0 && (
        <EmailChipsPopup
          editorRef={editorRef}
          viewPopUps={viewPopUps}
          setViewPopUps={setViewPopUps}
          availableFields={getAvailableFields(workflowCustomVariables, userProfileFields)}
        />
      )}
    </div>
  );
};

export default EmailBodyEditor;
